import { useState, useEffect, useMemo } from 'react';
import { TrendingUp, Clock, Vault, Activity, DollarSign, Users, FileText, Sparkles, X, Droplets, ExternalLink, RefreshCw, CheckCircle, XCircle } from 'lucide-react';
import type { Invoice, InvoiceStats as InvoiceStatsType } from '@/types/invoice';
import { useAccount, useWatchContractEvent, useBalance } from 'wagmi';
import { MEZO_CONTRACTS, INVOICE_CONTRACT_ABI, BORROW_MANAGER_ABI } from '@/lib/mezo';
import { useMezoVault } from '@/hooks/useMezoVault';
import { paymentMonitor, PaymentEvent } from '@/services/payment-monitor';
import { transactionStorage } from '@/services/transaction-storage';
import { matsRewards } from '@/services/mats-rewards';
import { useWalletUtils } from '@/hooks/useWalletUtils';
import { formatEther } from 'viem';

type InvoiceStats = InvoiceStatsType;

interface DashboardProps {
  onNavigate: (tab: string) => void;
  invoices: Invoice[];
  stats: InvoiceStats;
}

export function Dashboard({ onNavigate, invoices, stats }: DashboardProps) {
  const { address, isConnected: _isConnected, chainId } = useAccount();
  const { isMezoTestnet } = useWalletUtils();
  const { refetchAll: refetchVault } = useMezoVault();
  const [refreshTrigger, setRefreshTrigger] = useState(0);
  const [bitcoinPrice, setBitcoinPrice] = useState<number>(0);
  const [isLoadingPrice, setIsLoadingPrice] = useState(true);
  const [totalMats, setTotalMats] = useState(0);
  const [showFaucetBanner, setShowFaucetBanner] = useState(true);
  const [autoRefresh, setAutoRefresh] = useState(true);
  const [lastRefresh, setLastRefresh] = useState<Date>(new Date());

  // Check wallet balance for testnet faucet banner
  const { data: walletBalance } = useBalance({
    address: address,
    query: {
      enabled: !!address && isMezoTestnet(chainId),
    },
  });
  
  const balanceBTC = walletBalance ? parseFloat(formatEther(walletBalance.value)) : 0;
  const needsFaucet = isMezoTestnet(chainId) && balanceBTC < 0.001; // Less than 0.001 BTC
  
  // Check if promo card should be shown (stored in localStorage)
  const [showPromoCard, setShowPromoCard] = useState(() => {
    if (typeof window !== 'undefined') {
      const saved = localStorage.getItem('dashboard-promo-dismissed');
      return saved !== 'true';
    }
    return true;
  });

  // Check if faucet banner should be shown
  useEffect(() => {
    if (typeof window !== 'undefined') {
      const dismissed = localStorage.getItem('dashboard-faucet-banner-dismissed');
      if (dismissed === 'true') {
        setShowFaucetBanner(false);
      }
    }
  }, []);

  const handleDismissPromo = () => {
    setShowPromoCard(false);
    if (typeof window !== 'undefined') {
      localStorage.setItem('dashboard-promo-dismissed', 'true');
    }
  };

  // Watch invoice events for real-time updates
  useWatchContractEvent({
    address: MEZO_CONTRACTS.INVOICE_CONTRACT as `0x${string}`,
    abi: INVOICE_CONTRACT_ABI,
    eventName: 'InvoiceCreated',
    onLogs: () => {
      console.log('📊 Dashboard: InvoiceCreated detected, triggering refresh');
      setRefreshTrigger((v) => v + 1);
    }
  });

  useWatchContractEvent({
    address: MEZO_CONTRACTS.INVOICE_CONTRACT as `0x${string}`,
    abi: INVOICE_CONTRACT_ABI,
    eventName: 'InvoicePaid',
    onLogs: () => {
      console.log('📊 Dashboard: InvoicePaid detected, triggering refresh');
      setRefreshTrigger((v) => v + 1);
    }
  });

  useWatchContractEvent({
    address: MEZO_CONTRACTS.INVOICE_CONTRACT as `0x${string}`,
    abi: INVOICE_CONTRACT_ABI,
    eventName: 'InvoiceApproved',
    onLogs: () => {
      console.log('📊 Dashboard: InvoiceApproved detected, triggering refresh');
      setRefreshTrigger((v) => v + 1);
    }
  });

  useWatchContractEvent({
    address: MEZO_CONTRACTS.INVOICE_CONTRACT as `0x${string}`,
    abi: INVOICE_CONTRACT_ABI,
    eventName: 'InvoiceCancelled',
    onLogs: () => {
      console.log('📊 Dashboard: InvoiceCancelled detected, triggering refresh');
      setRefreshTrigger((v) => v + 1);
    }
  });

  // Watch vault events for real-time updates
  useWatchContractEvent({
    address: MEZO_CONTRACTS.MEZO_VAULT as `0x${string}`,
    abi: BORROW_MANAGER_ABI,
    eventName: 'CollateralDeposited',
    onLogs: () => {
      console.log('📊 Dashboard: CollateralDeposited detected, triggering vault refresh');
      refetchVault();
    }
  });

  useWatchContractEvent({
    address: MEZO_CONTRACTS.MEZO_VAULT as `0x${string}`,
    abi: BORROW_MANAGER_ABI,
    eventName: 'MUSDBorrowed',
    onLogs: () => {
      console.log('📊 Dashboard: MUSDBorrowed detected, triggering vault refresh');
      refetchVault();
    }
  });

  useWatchContractEvent({
    address: MEZO_CONTRACTS.MEZO_VAULT as `0x${string}`,
    abi: BORROW_MANAGER_ABI,
    eventName: 'MUSDRepaid',
    onLogs: () => {
      console.log('📊 Dashboard: MUSDRepaid detected, triggering vault refresh');
      refetchVault();
    }
  });

  useWatchContractEvent({
    address: MEZO_CONTRACTS.MEZO_VAULT as `0x${string}`,
    abi: BORROW_MANAGER_ABI,
    eventName: 'CollateralWithdrawn',
    onLogs: () => {
      console.log('📊 Dashboard: CollateralWithdrawn detected, triggering vault refresh');
      refetchVault();
    }
  });

  // Watch payment monitor events
  useEffect(() => {
    const handlePaymentEvent = (event: PaymentEvent) => {
      if (event.type === 'payment_detected' || event.type === 'payment_confirmed') {
        console.log('📊 Dashboard: Payment event detected, triggering refresh');
        setRefreshTrigger((v) => v + 1);
      }
    };

    paymentMonitor.setCallbacks({
      onPaymentDetected: handlePaymentEvent,
      onPaymentConfirmed: handlePaymentEvent,
    });

    return () => {
      paymentMonitor.setCallbacks({});
    };
  }, []);

  // Fetch Bitcoin price for USD conversion
  useEffect(() => {
    let cancelled = false;
    let intervalId: NodeJS.Timeout | null = null;

    const fetchBitcoinPrice = async () => {
      try {
        setIsLoadingPrice(true);
        const { fetchBitcoinPrice: fetchPrice } = await import('@/lib/bitcoin-price');
        const price = await fetchPrice();
        if (!cancelled) {
          setBitcoinPrice(price);
          setIsLoadingPrice(false);
        }
      } catch (error) {
        console.error('Error fetching Bitcoin price:', error);
        if (!cancelled) {
          setBitcoinPrice(65000); // Fallback price
          setIsLoadingPrice(false);
        }
      }
    };

    fetchBitcoinPrice();
    // Refresh every 30 seconds
    intervalId = setInterval(fetchBitcoinPrice, 30000);

    return () => {
      cancelled = true;
      if (intervalId) {
        clearInterval(intervalId);
      }
    };
  }, []);

  // Listen for transaction updates (same as Payments page)
  const [transactionVersion, setTransactionVersion] = useState(0);
  useEffect(() => {
    const onStorageUpdate = () => setTransactionVersion((v) => v + 1);
    if (typeof window !== 'undefined') {
      window.addEventListener('transactions_updated', onStorageUpdate);
    }
    return () => {
      if (typeof window !== 'undefined') {
        window.removeEventListener('transactions_updated', onStorageUpdate);
      }
    };
  }, []);

  // Force re-render when refresh trigger changes (to update stats)
  useEffect(() => {
    // This effect ensures the component re-renders when events fire
    // The parent App component will pass updated invoices/stats via props
  }, [refreshTrigger, transactionVersion]);

  // Auto-refresh every 30 seconds if enabled
  useEffect(() => {
    if (!autoRefresh) return;
    
    const interval = setInterval(() => {
      setRefreshTrigger((v) => v + 1);
      setLastRefresh(new Date());
    }, 30000);

    return () => clearInterval(interval);
  }, [autoRefresh]);

  // Manual refresh function
  const handleRefresh = () => {
    setRefreshTrigger((v) => v + 1);
    setLastRefresh(new Date());
  };

  // Calculate real revenue from paid invoices in BTC, then convert to USD
  // Use same logic as Payments page to ensure consistency
  const totalRevenueBTC = useMemo(() => {
    // Calculate from invoices (sum in BTC) - match Payments page logic exactly
    const paidInvoices = invoices.filter(invoice => invoice.status === 'paid');
    console.log('📊 Dashboard: Calculating revenue from', paidInvoices.length, 'paid invoices');
    
    const calculatedBTC = paidInvoices.reduce((sum, invoice) => {
      // Priority 1: Use observedInboundAmount (actual received in wei) - same as Payments
      let amountBtc = invoice.amount; // fallback to requested amount
      try {
        const observed = (invoice as any).observedInboundAmount as string | undefined;
        if (observed && observed !== '0' && observed !== '') {
          amountBtc = Number(BigInt(observed)) / Math.pow(10, 18);
          console.log('📊 Dashboard: Invoice', invoice.id, 'observedInboundAmount:', observed, '→', amountBtc, 'BTC');
          return sum + amountBtc;
        }
        
        // Priority 2: Try transaction amount (same as Payments page)
        const transactions = address ? transactionStorage.getTransactionsForInvoice(address, invoice.id) : [];
        const latestTransaction = transactions[0];
        if (latestTransaction?.amount) {
          amountBtc = Number(BigInt(latestTransaction.amount)) / Math.pow(10, 18);
          console.log('📊 Dashboard: Invoice', invoice.id, 'transaction amount:', latestTransaction.amount, '→', amountBtc, 'BTC');
          return sum + amountBtc;
        }
      } catch (e) {
        console.warn('📊 Dashboard: Failed to parse observedInboundAmount/transaction, using invoice.amount:', e);
      }
      
      // Priority 3: Fallback to invoice.amount (already in BTC)
      console.log('📊 Dashboard: Invoice', invoice.id, 'using invoice.amount:', amountBtc, 'BTC');
      return sum + amountBtc;
    }, 0);
    
    console.log('📊 Dashboard: Total revenue calculated:', calculatedBTC, 'BTC');
    return calculatedBTC;
  }, [invoices, transactionVersion]);

  // Convert BTC to USD using current Bitcoin price
  const totalRevenue = useMemo(() => {
    return totalRevenueBTC * (bitcoinPrice || 0);
  }, [totalRevenueBTC, bitcoinPrice]);

  const activeInvoices = useMemo(() => {
    // Use stats if available, otherwise calculate locally - only count truly pending (exclude cancelled and expired)
    if (stats?.activeInvoices !== undefined) {
      return stats.activeInvoices;
    }
    const now = Date.now();
    return invoices.filter(invoice => {
      if (invoice.status !== 'pending') return false;
      // Check if invoice has expired
      if (invoice.expiresAt) {
        const isExpired = new Date(invoice.expiresAt).getTime() <= now;
        if (isExpired) return false; // Don't count expired as pending
      }
      return true; // Only count if status is pending and not expired
    }).length;
  }, [invoices, stats?.activeInvoices]);

  const totalInvoices = useMemo(() => {
    return stats?.totalInvoices ?? invoices.length;
  }, [invoices, stats?.totalInvoices]);

  // pending amount is computed upstream and not displayed here

  // Calculate paid invoices count and collection metrics
  const paidInvoicesCount = useMemo(() => {
    return invoices.filter(invoice => invoice.status === 'paid').length;
  }, [invoices]);

  // Calculate collection rate (percentage of invoices paid)
  const collectionRate = useMemo(() => {
    if (totalInvoices === 0) return 0;
    return Math.round((paidInvoicesCount / totalInvoices) * 100);
  }, [paidInvoicesCount, totalInvoices]);

  // Fetch MATS total
  useEffect(() => {
    const refreshMats = () => {
      if (address) {
        setTotalMats(matsRewards.getTotalMats(address));
      } else {
        setTotalMats(0);
      }
    };
    
    refreshMats();
    const interval = setInterval(refreshMats, 5000); // Refresh every 5 seconds
    window.addEventListener('matsUpdated', refreshMats);
    
    return () => {
      clearInterval(interval);
      window.removeEventListener('matsUpdated', refreshMats);
    };
  }, [address]);

  const statsData = [
    {
      label: 'Total Revenue',
      value: isLoadingPrice ? 'Loading...' : `$${totalRevenue.toFixed(2)}`,
      btcValue: totalRevenueBTC.toFixed(8),
      change: paidInvoicesCount > 0 ? `Earned from ${paidInvoicesCount} ${paidInvoicesCount === 1 ? 'invoice' : 'invoices'}` : 'No payments yet',
      icon: DollarSign,
      color: 'text-green-400',
      bgColor: 'bg-green-500/10',
    },
    {
      label: 'Active Invoices',
      value: activeInvoices.toString(),
      change: activeInvoices > 0 ? 'Awaiting payment' : 'None',
      icon: FileText,
      color: 'text-blue-400',
      bgColor: 'bg-blue-500/10',
    },
    {
      label: 'Total Invoices',
      value: totalInvoices.toString(),
      change: totalInvoices > 0 ? 'All time' : 'No invoices',
      icon: Users,
      color: 'text-purple-400',
      bgColor: 'bg-purple-500/10',
    },
    {
      label: 'Collection Rate',
      value: `${collectionRate}%`,
      change: totalInvoices > 0 ? `${paidInvoicesCount}/${totalInvoices} paid` : 'No invoices',
      icon: TrendingUp,
      color: 'text-orange-400',
      bgColor: 'bg-orange-500/10',
    },
    {
      label: 'MATS Rewards',
      value: totalMats.toLocaleString(),
      change: address ? 'From Vault & Market' : 'Connect wallet',
      icon: Sparkles,
      color: 'text-purple-400',
      bgColor: 'bg-purple-500/10',
    },
  ];

  const quickActions = [
    {
      label: 'Create Invoice',
      description: 'Generate invoices with Bitcoin payments',
      icon: TrendingUp,
      tab: 'invoices',
      emoji: '📝',
      gradient: 'from-blue-500 to-purple-600',
      bgColor: 'bg-blue-500/20',
      borderColor: 'border-blue-400/30',
      hoverBg: 'hover:bg-blue-500/30',
      hoverBorder: 'hover:border-blue-400/50',
      iconBg: 'bg-blue-500/10',
      iconBorder: 'border-blue-400/30',
      iconHover: 'group-hover:bg-blue-500/20',
      iconColor: 'text-blue-400',
    },
    {
      label: 'Track Payments',
      description: 'Monitor transactions and payments',
      icon: Clock,
      tab: 'payments',
      emoji: '💸',
      gradient: 'from-green-500 to-teal-600',
      bgColor: 'bg-green-500/20',
      borderColor: 'border-green-400/30',
      hoverBg: 'hover:bg-green-500/30',
      hoverBorder: 'hover:border-green-400/50',
      iconBg: 'bg-green-500/10',
      iconBorder: 'border-green-400/30',
      iconHover: 'group-hover:bg-green-500/20',
      iconColor: 'text-green-400',
    },
    {
      label: 'Manage Vault',
      description: 'Deposit BTC and borrow MUSD',
      icon: Vault,
      tab: 'vault',
      emoji: '🔐',
      gradient: 'from-orange-500 to-red-600',
      bgColor: 'bg-orange-500/20',
      borderColor: 'border-orange-400/30',
      hoverBg: 'hover:bg-orange-500/30',
      hoverBorder: 'hover:border-orange-400/50',
      iconBg: 'bg-orange-500/10',
      iconBorder: 'border-orange-400/30',
      iconHover: 'group-hover:bg-orange-500/20',
      iconColor: 'text-orange-400',
    },
    {
      label: 'View Analytics',
      description: 'Business insights and trends',
      icon: Activity,
      tab: 'analytics',
      emoji: '📊',
      gradient: 'from-purple-500 to-pink-600',
      bgColor: 'bg-purple-500/20',
      borderColor: 'border-purple-400/30',
      hoverBg: 'hover:bg-purple-500/30',
      hoverBorder: 'hover:border-purple-400/50',
      iconBg: 'bg-purple-500/10',
      iconBorder: 'border-purple-400/30',
      iconHover: 'group-hover:bg-purple-500/20',
      iconColor: 'text-purple-400',
    },
  ];

  return (
    <div className="p-4 sm:p-6 lg:p-8 relative">
      {/* Subtle background pattern */}
      <div className="absolute inset-0 opacity-5 pointer-events-none">
        <div className="w-full h-full" style={{
          backgroundImage: `
            linear-gradient(rgba(255, 255, 255, 0.01) 1px, transparent 1px),
            linear-gradient(90deg, rgba(255, 255, 255, 0.01) 1px, transparent 1px)
          `,
          backgroundSize: '20px 20px',
        }} />
      </div>
      
      <div className="relative z-10">
        {/* Header */}
        <div className="mb-6">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-2">
            <div className="flex items-center gap-3">
              <i className="fa-solid fa-house text-white text-2xl sm:text-3xl"></i>
              <h1 className="text-2xl sm:text-3xl font-bold font-navbar text-white">
                Dashboard
              </h1>
            </div>
            <div className="flex items-center gap-3">
              <button
                onClick={() => setAutoRefresh(!autoRefresh)}
                className={`flex items-center gap-2 px-3 py-2 rounded-lg border transition-all text-sm font-navbar ${
                  autoRefresh 
                    ? 'bg-green-500/10 border-green-400/50 text-green-400' 
                    : 'bg-white/5 border-white/20 text-white/60 hover:bg-white/10'
                }`}
              >
                <RefreshCw className={`w-4 h-4 ${autoRefresh ? 'animate-spin' : ''}`} />
                Auto-refresh
              </button>
              <button
                onClick={handleRefresh}
                className="flex items-center gap-2 px-3 py-2 rounded-lg bg-blue-500/10 border border-blue-400/50 text-blue-400 hover:bg-blue-500/20 transition-all text-sm font-navbar"
              >
                <RefreshCw className="w-4 h-4" />
                Refresh
              </button>
            </div>
          </div>
          <p className="text-sm font-navbar text-white/60">Welcome back! Here's what's happening with your business.</p>
          <p className="text-xs font-navbar text-white/40 mt-1">
            Last updated: {lastRefresh.toLocaleTimeString()}
          </p>
        </div>

        {/* Bitcoin & MUSD Statement - Compact & Dismissible */}
        {showPromoCard && (
          <div className="mb-4 relative group">
            <div className="p-3 sm:p-4 rounded-lg bg-gradient-to-r from-orange-500/10 via-orange-600/5 to-purple-500/10 border border-orange-400/20 backdrop-blur-xl">
              <button
                onClick={handleDismissPromo}
                className="absolute top-2 right-2 p-1 rounded-md hover:bg-white/10 transition-colors opacity-60 hover:opacity-100"
                aria-label="Dismiss"
              >
                <X className="w-4 h-4 text-white/70" />
              </button>
              <p className="text-sm font-medium font-navbar text-white/90 pr-6">
                💎 <span className="text-orange-300">Bitcoin-backed MUSD</span> – Unlock liquidity from your BTC without selling. 
                <span className="text-purple-300"> Borrow stablecoins</span>, <span className="text-green-300">accept BTC payments</span>, 
                <span className="text-blue-300"> grow your business</span>.
              </p>
            </div>
          </div>
        )}

        {/* Mezo Testnet Faucet Banner - Only shows on testnet and if balance is low */}
        {isMezoTestnet(chainId) && showFaucetBanner && needsFaucet && address && (
          <div className="mb-4 relative group">
            <div className="p-4 rounded-xl bg-gradient-to-r from-cyan-500/20 via-blue-500/10 to-purple-500/10 border-2 border-cyan-400/30 backdrop-blur-xl shadow-lg shadow-cyan-500/10">
              <button
                onClick={() => {
                  setShowFaucetBanner(false);
                  localStorage.setItem('dashboard-faucet-banner-dismissed', 'true');
                }}
                className="absolute top-2 right-2 p-1 rounded-md hover:bg-white/10 transition-colors opacity-60 hover:opacity-100"
                aria-label="Dismiss"
              >
                <X className="w-4 h-4 text-white/70" />
              </button>
              <div className="flex items-start gap-3 pr-8">
                <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-cyan-400 to-blue-500 flex items-center justify-center shadow-lg shadow-cyan-500/20 flex-shrink-0">
                  <Droplets className="w-6 h-6 text-white" />
                </div>
                <div className="flex-1">
                  <h3 className="text-base font-bold font-navbar text-white mb-1">Need Testnet BTC?</h3>
                  <p className="text-sm font-navbar text-white/80 mb-3">
                    Get free testnet BTC from the Mezo faucet to create invoices, use the vault, and test all features. 
                    Your balance: <span className="font-semibold text-cyan-300">{balanceBTC.toFixed(6)} BTC</span>
                  </p>
                  <a
                    href="https://faucet.test.mezo.org/"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-2 px-4 py-2.5 bg-gradient-to-r from-cyan-500/30 to-blue-500/30 hover:from-cyan-500/40 hover:to-blue-500/40 border border-cyan-400/50 hover:border-cyan-400/70 rounded-lg transition-all text-sm font-semibold font-navbar text-white group"
                  >
                    <Droplets className="w-4 h-4" />
                    <span>Get Testnet BTC</span>
                    <ExternalLink className="w-3.5 h-3.5 group-hover:translate-x-0.5 transition-transform" />
                  </a>
                  <p className="text-xs font-navbar text-white/60 mt-2">
                    Make sure you're connected to <strong>Mezo Testnet</strong> (Chain ID: 31611)
                  </p>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Stats Overview */}
        <div className="mb-8">
          <h2 className="text-lg font-semibold mb-4 font-navbar text-white/90">Business Overview</h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-3 sm:gap-4">
            {statsData.map((stat) => (
              <div key={stat.label} className="bg-[#2C2C2E]/40 backdrop-blur-xl border border-green-400/10 rounded-xl sm:rounded-2xl p-3 sm:p-4 hover:border-green-400/20 transition-all active:scale-[0.98] touch-manipulation">
                <div className="flex items-center gap-2 sm:gap-3 mb-2 sm:mb-3">
                  <div className="w-8 h-8 sm:w-10 sm:h-10 rounded-lg border border-green-400/30 flex items-center justify-center shrink-0">
                    <stat.icon className="w-4 h-4 sm:w-5 sm:h-5 text-white" />
                  </div>
                  <span className={`text-xs font-navbar font-medium ${
                    stat.label === 'Total Revenue' && paidInvoicesCount > 0 ? 'text-green-400' :
                    stat.label === 'Active Invoices' && activeInvoices > 0 ? 'text-blue-400' :
                    stat.label === 'Collection Rate' && collectionRate > 0 ? 'text-orange-400' :
                    'text-gray-400'
                  }`}>
                    {stat.change}
                  </span>
                </div>
                <div>
                  <p className="text-xl font-bold font-navbar mb-1">{stat.value}</p>
                  {(stat as any).btcValue && (
                    <p className="text-[10px] font-navbar text-white/50 mb-1">{(stat as any).btcValue} BTC</p>
                  )}
                  <p className="text-xs font-navbar text-white/60">{stat.label}</p>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Quick Actions */}
        <div className="mb-8">
          <h2 className="text-lg font-semibold mb-4 font-navbar text-white/90">Quick Actions</h2>
          <div className="flex flex-wrap gap-2 sm:gap-3">
            {quickActions.map((action) => (
              <div key={action.label} className="relative group">
                <button
                  onClick={() => onNavigate(action.tab)}
                  className={`flex items-center gap-2 sm:gap-3 px-3 sm:px-4 py-2.5 sm:py-3 min-h-[44px] ${action.bgColor} border ${action.borderColor} rounded-full ${action.hoverBg} ${action.hoverBorder} transition-all duration-200 group active:scale-95 touch-manipulation`}
                >
                  <div className={`w-7 h-7 sm:w-8 sm:h-8 rounded-lg ${action.iconBg} border ${action.iconBorder} flex items-center justify-center ${action.iconHover} transition-all shrink-0`}>
                    <action.icon className={`w-3.5 h-3.5 sm:w-4 sm:h-4 text-white`} />
                  </div>
                  <span className="text-xs sm:text-sm font-navbar text-white/80 group-hover:text-white transition-colors">
                    {action.label}
                  </span>
                </button>
                {/* Tooltip */}
                <div className="absolute top-full left-1/2 transform -translate-x-1/2 mt-2 px-3 py-2 bg-gray-800/95 backdrop-blur-sm text-white text-xs rounded-lg opacity-0 group-hover:opacity-100 transition-all duration-200 pointer-events-none z-50 shadow-lg border border-gray-600/30">
                  {action.description}
                  <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 w-0 h-0 border-l-3 border-r-3 border-b-3 border-transparent border-b-gray-800/95"></div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Recent Activity */}
        <div className="mb-8">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-semibold font-navbar text-white/90">Recent Activity</h2>
            <span className="text-xs font-navbar text-white/60">
              {invoices.length > 0 ? `Showing latest ${Math.min(invoices.length, 10)} of ${invoices.length}` : 'No activity yet'}
            </span>
          </div>
          <div className="bg-[#2C2C2E]/40 backdrop-blur-xl border border-white/10 rounded-2xl p-6">
            {invoices.length > 0 ? (
              <div className="space-y-3 max-h-[400px] overflow-y-auto pr-2">
                {invoices.slice(0, 10).map((invoice) => {
                  const getActivityIcon = () => {
                    switch (invoice.status) {
                      case 'paid':
                        return <CheckCircle className="w-4 h-4 text-green-400" />;
                      case 'cancelled':
                        return <XCircle className="w-4 h-4 text-red-400" />;
                      default:
                        return <Clock className="w-4 h-4 text-blue-400" />;
                    }
                  };

                  const getActivityText = () => {
                    switch (invoice.status) {
                      case 'paid':
                        return `Invoice ${invoice.id} paid`;
                      case 'cancelled':
                        return `Invoice ${invoice.id} cancelled`;
                      default:
                        return `Invoice ${invoice.id} created`;
                    }
                  };

                  const getActivityColor = () => {
                    switch (invoice.status) {
                      case 'paid':
                        return 'border-green-400/30 bg-gradient-to-r from-green-500/10 to-emerald-400/5 hover:from-green-500/15 hover:to-emerald-400/10';
                      case 'cancelled':
                        return 'border-red-400/30 bg-gradient-to-r from-red-500/10 to-pink-400/5 hover:from-red-500/15 hover:to-pink-400/10';
                      default:
                        return 'border-blue-400/30 bg-gradient-to-r from-blue-500/10 to-cyan-400/5 hover:from-blue-500/15 hover:to-cyan-400/10';
                    }
                  };

                  return (
                    <div key={invoice.id} className={`group flex items-center gap-3 p-3 rounded-xl border transition-all duration-200 hover:scale-[1.01] hover:shadow-lg ${getActivityColor()}`}>
                      <div className="w-8 h-8 rounded-lg bg-white/5 border border-white/10 flex items-center justify-center flex-shrink-0 group-hover:scale-110 transition-transform">
                        {getActivityIcon()}
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-semibold font-navbar text-white truncate mb-0.5">
                          {getActivityText()}
                        </p>
                        <div className="flex items-center gap-2">
                          <p className="text-xs font-navbar text-white/60">
                            {new Date(invoice.createdAt).toLocaleDateString()} • {invoice.clientName}
                          </p>
                          {invoice.amount && (
                            <>
                              <span className="text-xs text-white/40">•</span>
                              <span className="text-xs font-mono font-navbar text-white/50">
                                {invoice.amount} BTC
                              </span>
                            </>
                          )}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            ) : (
              <div className="text-center py-8">
                <FileText className="w-12 h-12 text-white/20 mx-auto mb-4" />
                <h3 className="text-lg font-bold font-navbar text-white mb-2">No Activity Yet</h3>
                <p className="text-sm font-navbar text-white/60 mb-4">
                  Your invoice activity will appear here once you create invoices.
                </p>
                <button
                  onClick={() => onNavigate('invoices')}
                  className="inline-flex items-center gap-2 px-4 py-2 bg-blue-500/10 border border-blue-400/50 rounded-lg text-blue-400 hover:bg-blue-500/20 transition-colors text-sm font-navbar"
                >
                  <FileText className="w-4 h-4" />
                  Create Your First Invoice
                </button>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
